/**
 * 中文扑克术语映射 — 中文化优先的核心基础
 * 目标用户：完全不懂 GTO 的新手。所有英文术语优先用中文表达。
 */

/**
 * 术语白话释义条目(新手化点词释义的最小单元)。
 * 权威定义在本文件(单一之家);quiz-gloss-zh 从本文件 re-export 此类型。
 */
export interface GlossEntry {
  term: string;
  gloss: string;
}

export const POSITION_ZH: Record<string, string> = {
  UTG: "枪口位",
  "UTG+1": "枪口+1",
  UTG1: "枪口位",
  LJ: "劫机位",
  HJ: "劫持位",
  MP: "中位",
  CO: "关煞位",
  BTN: "庄位",
  Button: "庄位",
  SB: "小盲",
  BB: "大盲",
  EP: "前位",
  MP1: "中位",
  LP: "后位",
};

export const STREET_ZH: Record<string, string> = {
  preflop: "翻前",
  flop: "翻牌",
  turn: "转牌",
  river: "河牌",
  multi: "多街",
  showdown: "摊牌",
};

export const ACTION_ZH: Record<string, string> = {
  fold: "弃牌",
  check: "过牌",
  call: "跟注",
  bet: "下注",
  raise: "加注",
  allin: "全下",
  "all-in": "全下",
  cbet: "持续下注",
  "c-bet": "持续下注",
  "3bet": "再加注",
  "3-bet": "再加注",
  "4bet": "四加注",
  "4-bet": "四加注",
  "5bet": "五加注",
  "check-raise": "过牌加注",
  "check-call": "过牌跟注",
  limp: "平跟入池",
  donk: "领打",
  "check-back": "过牌回让",
  "bet-fold": "下注弃牌",
  "bet-call": "下注跟注",
};

export const CONCEPT_ZH: Record<string, string> = {
  bluff: "诈唬",
  "semi-bluff": "半诈唬",
  "value-bet": "价值下注",
  "pot-odds": "底池赔率",
  "implied-odds": "隐含赔率",
  equity: "胜率权益",
  range: "手牌范围",
  position: "位置优势",
  blockers: "阻隔牌",
  "flush-draw": "同花听牌",
  "straight-draw": "顺子听牌",
  "gut-shot": "卡顺听牌",
  "open-ended": "两头顺听牌",
  overpair: "超对",
  "top-pair": "顶对",
  "top-pair-top-kicker": "顶对顶踢",
  set: "暗三条",
  trips: "明三条",
  "two-pair": "两对",
  "full-house": "葫芦",
  quads: "四条",
  nuts: "坚果牌",
  "second-nuts": "第二坚果",
  "pot-control": "底池控制",
  "slow-play": "慢打",
  "check-raise": "过牌加注",
  "pot-commit": "底池承诺",
  "stack-to-pot": "筹码底池比",
  spr: "筹码底池比",
  "reverse-implied": "反向隐含赔率",
  "fold-equity": "弃牌权益",
  "board-texture": "牌面结构",
  "dry-board": "干燥牌面",
  "wet-board": "潮湿牌面",
  "connected-board": "连接牌面",
  "paired-board": "对子牌面",
  "backdoor": "后门听牌",
  "combo-draw": "组合听牌",
  "thin-value": "薄价值",
  "probe-bet": "试探下注",
  "delayed-cbet": "延迟持续下注",
  "double-barrel": "双管齐下",
  "triple-barrel": "三管齐下",
  "overbet": "超池下注",
  "blocker-bet": "阻隔下注",
  "polarized": "两极化范围",
  "merged": "融合范围",
  "linear": "线性范围",
  "squeeze": "挤压加注",
  "isolation": "隔离加注",
  resteal: "反偷盲",
  "steal": "偷盲",
  "squeeze-play": "挤压打法",
};

export const SCENARIO_ZH: Record<string, string> = {
  cbet: "持续下注",
  "3bet_pot": "3bet 底池",
  "3bet-pot": "3bet 底池",
  bluff_catch: "抓诈唬",
  overbet: "超池下注",
  slowplay: "慢打",
  check_raise: "过牌加注",
  "check-raise": "过牌加注",
  SRP: "单一加注底池",
  srp: "单一加注底池",
  limped_pot: "平跟底池",
  multiway: "多人底池",
  heads_up: "单挑",
  "blind-vs-blind": "盲注对战",
  "bvb": "盲注对战",
};

/**
 * BB 有两个义项,新手被「每屏必卡」的真因就是三本词典只给了位置义、屏上却是单位义:
 * - 位置义「大盲位」= 翻前被强制下注一个盲注的座位;
 * - 单位义「筹码单位」= 1 BB 一个大盲注的筹码量,屏上 pot/stacks 的「6 BB」即此义。
 */
export const BB_POSITION_GLOSS =
  "大盲位：翻前被强制下注一个盲注的座位，开局不用表态就能看牌，但翻后位置最差";
export const BB_UNIT_GLOSS =
  "筹码单位：1 BB = 一个大盲注的筹码量。屏上「6 BB」就是 6 个大盲注的筹码，用来衡量底池和后手有多大";

export type BbSense = "position" | "unit";

/** 按上下文选 BB 义:数字语境(底池/后手「X BB」)走单位义,座位语境走位置义。 */
export function bbGloss(sense: BbSense): string {
  return sense === "unit" ? BB_UNIT_GLOSS : BB_POSITION_GLOSS;
}

/** 屏上「X BB」数字语境复用的单位义词条(供渲染层包成可点 chip)。 */
export const BB_UNIT_GLOSS_ENTRY: GlossEntry = { term: "BB", gloss: BB_UNIT_GLOSS };

/**
 * 权威白话释义表(单一之家)。键 = 术语原形(中文术语 + 受认可英文缩写),值 = 新手白话。
 * quiz-gloss-zh 不再自带 gloss 文本,统一从本表取义,杜绝同一术语多处冲突。
 *
 * 覆盖三块:
 * 1. 五张 zh 映射表的全部中文值与英文键(由 GLOSS_BY_ZH 派生,保证白名单 ⊆ 词表);
 * 2. EXTRA 受认可缩写(SPR/OOP/IP/TPTK/Hero/Villain);
 * 3. 词表外的教学补充词(新手实测卡壳词,如 冷跟/中对/连张/彩虹/单色/双色)。
 *
 * BB 双义:英文键「BB」走单位义(屏上数字语境),中文「大盲」走位置义(座位语境)。
 */
const GLOSS_BY_ZH: Record<string, string> = {
  // 位置
  枪口位: "翻前第一个行动的位置，信息最少，起手牌要最紧",
  "枪口+1": "枪口位的下一位，同样很早行动，牌要很紧",
  劫机位: "比枪口位晚两位，仍属偏早的位置",
  劫持位: "中间偏前的位置，比枪口位可以打得宽松一些",
  中位: "中间位置，起手牌可以比前位略宽",
  关煞位: "庄位前一位，位置较好，可打得较宽",
  庄位: "庄家位（BTN），翻后最后行动，位置最好、信息最多（是位置名，不是屏幕按钮）",
  小盲: "翻前强制下注半个盲注的位置，翻后最先行动、最被动",
  大盲: BB_POSITION_GLOSS,
  前位: "靠前行动的位置统称，信息少、要打得紧",
  后位: "靠后行动的位置统称，信息多、可打得更宽",
  // 街
  翻前: "发完两张手牌、公共牌还没出时的阶段",
  翻牌: "发出前三张公共牌后的阶段",
  转牌: "发出第四张公共牌后的阶段",
  河牌: "发出第五张也是最后一张公共牌后的阶段",
  多街: "横跨多条街的一手牌",
  摊牌: "比牌阶段，亮出手牌比大小",
  // 动作
  弃牌: "放弃这手牌，不再投入筹码",
  过牌: "不加注，把决定权交给对手",
  跟注: "匹配当前下注额，继续留在牌局",
  下注: "主动投入筹码，建立底池或施压",
  加注: "提高当前的下注额",
  全下: "押上全部筹码",
  持续下注: "翻前的加注者，翻牌后继续下注",
  再加注: "对别人的加注再一次加注",
  四加注: "在 3-bet 之上再一次加注",
  五加注: "在 4-bet 之上再一次加注",
  过牌加注: "先过牌、等对手下注后再加注",
  过牌跟注: "先过牌、等对手下注后跟注",
  平跟入池: "翻前只跟入大盲、不加注",
  领打: "没等翻前加注者，自己抢先下注",
  过牌回让: "对手过牌后，你也过牌，让牌局进入下一条街",
  下注弃牌: "计划下注、若被加注就弃牌",
  下注跟注: "计划下注、若被加注就跟注",
  // 概念
  诈唬: "手里没什么牌却下注或加注，目的是逼对手弃牌",
  半诈唬: "现在还不是大牌、但有潜力成大牌时下的诈唬注",
  价值下注: "手牌很强，下注是为了让更弱的牌跟注赚钱",
  底池赔率: "跟注要花多少、能赢多少的比例，判断值不值得跟",
  隐含赔率: "算上后面还能再赢到的钱，值不值得跟",
  胜率权益: "你这手牌最终赢下底池的概率份额",
  手牌范围: "一个玩家在这种情况下可能拿到的所有牌的集合",
  位置优势: "行动顺序带来的优势：越靠后行动，掌握的信息越多",
  阻隔牌: "你手里的牌让对手更不可能拿到某种牌",
  同花听牌: "再来一张同花色就能成同花",
  顺子听牌: "再来一张就能成顺子",
  卡顺听牌: "只差中间一张特定点数才能成顺子",
  两头顺听牌: "差一张成顺子，且两头都能补到",
  超对: "你手里的一对，比所有公共牌都大",
  顶对: "你的一张手牌和公共牌里最大那张配成一对",
  顶对顶踢: "顶对，且踢脚牌是最大的",
  暗三条: "手里一对 + 公共牌来一张同样的，凑成三条",
  明三条: "手里一张 + 公共牌一对，凑成三条",
  两对: "凑出两个不同的对子",
  葫芦: "一个三条加一个对子",
  四条: "四张一样大的牌",
  坚果牌: "当前牌面下绝对最大的牌，没人能赢你",
  第二坚果: "仅次于坚果的第二大牌",
  底池控制: "故意把底池控小，避免拿中等牌打大底池",
  慢打: "拿着强牌故意示弱，诱使对手下注",
  底池承诺: "已投入太多筹码，相对底池再也弃不掉",
  筹码底池比: "剩余筹码相对底池的倍数，决定该打多大、能否弃牌",
  反向隐含赔率: "就算成牌也可能输给更大牌的风险",
  弃牌权益: "你下注时对手会弃牌的概率带来的价值",
  牌面结构: "公共牌的花色、点数分布特征",
  干燥牌面: "公共牌之间联系少，很难成顺子同花",
  潮湿牌面: "公共牌联系紧密，很多顺子同花可能",
  连接牌面: "公共牌点数相连，顺子可能性高",
  对子牌面: "公共牌里出现了一对",
  后门听牌: "还差两张牌才能成（如后门同花），转牌河牌都要中",
  组合听牌: "同时听顺子又听同花，补牌很多",
  薄价值: "只对很窄的更弱牌有价值时，仍下注榨取价值",
  试探下注: "翻前加注者过牌后，你趁机下注试探",
  延迟持续下注: "翻牌没下、转牌才下的持续下注",
  双管齐下: "翻牌下注后，转牌继续下注",
  三管齐下: "翻牌、转牌、河牌三条街连续下注",
  超池下注: "下注金额比当前底池还大",
  阻隔下注: "下个小注，阻止对手下大注",
  两极化范围: "范围里要么很强的牌、要么诈唬，没有中等牌",
  融合范围: "范围里强牌和中等牌混在一起",
  线性范围: "从最强往下连续的一段牌，没有断层",
  挤压加注: "有人加注又有人跟注时，大幅再加注把两人挤走",
  隔离加注: "加注赶跑旁人，只留一个弱手单挑",
  反偷盲: "对偷盲的人反手再加注",
  偷盲: "在靠后位置加注，想直接赢走盲注",
  挤压打法: "用挤压加注逼迫对手弃牌的打法",
  // 场景
  "3bet 底池": "翻前经过再加注（3-bet）形成的底池，通常更大、更激烈",
  抓诈唬: "用一手只能赢过诈唬的牌跟注，抓对手的诈唬",
  单一加注底池: "翻前只有一次加注、没人再加注的底池",
  平跟底池: "翻前只有平跟、没有加注的底池",
  多人底池: "三个及以上玩家一起看的底池",
  单挑: "只剩两个玩家对决的底池",
  盲注对战: "小盲和大盲两个盲注位之间的对决",
};

/**
 * 词表外教学补充词:不在白名单派生的五表里(故不影响白名单与 SKILL.md 快照),
 * 但新手实测会卡壳,补进权威词表供点词释义。含 quiz 内联释义沿用的小写缩写与中文俗称。
 */
const EXTRA_GLOSS: Record<string, string> = {
  // 牌面花色结构(新手读牌必懵)
  彩虹: "彩虹牌面：三张公共牌花色各不相同，没法直接听同花",
  单色: "单色牌面：三张公共牌全是同一花色，同花已经在牌面上了",
  双色: "双色牌面：三张公共牌含两种花色，仍可能听同花",
  // 手牌/牌型俗称
  连张: "连张：两张点数相连的手牌（如 8-9），最容易成顺子",
  中对: "中对：你凑成的对子，点数在公共牌里算中等（不是最大也不是最小）",
  边牌: "边牌：对子一样大时，比谁手里另一张单牌更大（决定输赢的配角牌）",
  踢脚: "踢脚：对子一样大时，比谁手里另一张单牌更大（决定输赢的配角牌）",
  高牌: "高牌：还没凑成对子，手里最大的单牌",
  顺子: "顺子：五张点数连续的牌（如 5-6-7-8-9）",
  很牢: "很牢：口语，形容对手打法很稳、不轻易弃牌（很难诈唬走）",
  T: "T：牌面点数 10 的记法（T = Ten，如 T9 = 10 和 9）",
  三条: "三张一样大的牌",
  坚果: "当前最大的牌，没人能赢你",
  两头顺: "两头听顺：差一张成顺子，且两头都能补",
  听牌: "还差一张就能成顺子、同花等大牌",
  抽花: "听同花：再来一张同花色就成同花",
  抽顺: "听顺子：再来一张就成顺子",
  公对: "公共牌自己出现一对（人人都能用）",
  天花: "公共牌已能凑出最大同花",
  天顺: "公共牌已能凑出最大顺子",
  盖帽: "公共牌出现比你手里对子更大的牌，你的对子可能不再最大",
  // 动作俗称
  冷跟: "冷跟：之前没加过注，直接跟注别人的加注",
  超池: "下注金额比当前底池还大",
  "过牌-加注": "先过牌、等对手下注后再加注",
  isolate: "隔离：加注赶跑旁人，只跟一个弱手单挑",
  // 策略/数学俗称
  位置: "你在这一轮的行动顺序，越靠后越有信息优势",
  范围: "一个玩家可能拿到的所有牌的集合",
  尺度: "下注下多大（如半池、满池）",
  sizing: "下注下多大",
  频率: "某个动作该多常做（如 60% 下注、40% 过牌）",
  权益: "你这手牌最终赢下底池的概率份额",
  弃牌率: "你下注时对手会弃牌的概率",
  极化: "极化范围：要么很强、要么是诈唬，没有中间牌",
  混合策略: "同一手牌按一定比例，有时这么打、有时那么打",
  后门: "后门听牌：还差两张才成（如后门同花）",
  阻塞: "你手里的牌让对手更不可能拿到某种牌",
  blocker: "你手里的牌让对手更不可能拿到某种牌",
  赔率: "跟注要花多少、能赢多少的比例，判断值不值得跟",
  // GTO/比赛缩写
  MDF: "最低防守频率：对手下注时你至少要跟或加的比例，免得被白白诈唬",
  ICM: "把筹码换算成奖金价值的模型（比赛里影响该不该冒险）",
  EV: "期望值：一个打法长期平均能赚或亏多少",
  Outs: "补牌：能让你成牌的牌还有几张",
  所罗门: "所罗门法则：算补牌胜率时的一个小修正",
};

function buildAuthoritativeGloss(): Record<string, string> {
  const gloss: Record<string, string> = {};
  for (const table of [POSITION_ZH, STREET_ZH, ACTION_ZH, CONCEPT_ZH, SCENARIO_ZH]) {
    for (const [en, zh] of Object.entries(table)) {
      const text = GLOSS_BY_ZH[zh];
      if (!text) continue;
      gloss[en] = text;
      gloss[zh] = text;
    }
  }
  // BB 双义覆盖:英文键走单位义(屏上「X BB」数字语境),中文「大盲」保持位置义
  gloss.BB = BB_UNIT_GLOSS;
  gloss["大盲"] = BB_POSITION_GLOSS;
  // EXTRA 受认可英文缩写
  gloss.SPR = GLOSS_BY_ZH["筹码底池比"];
  gloss.TPTK = GLOSS_BY_ZH["顶对顶踢"];
  gloss.OOP = "不利位置：这一轮要先行动，信息少、被动";
  gloss.IP = "有利位置：这一轮后行动，信息多、主动";
  gloss.Hero = "你（讲解牌局时指主角玩家）";
  gloss.Villain = "对手（讲解牌局时指对手玩家）";
  for (const [term, text] of Object.entries(EXTRA_GLOSS)) gloss[term] = text;
  return gloss;
}

/** 权威白话释义表(只读)。术语原形 → 新手白话。点词释义的唯一事实源。 */
export const AUTHORITATIVE_GLOSS: Readonly<Record<string, string>> = buildAuthoritativeGloss();

/** 取术语的权威白话释义(trim 后精确匹配);未收录返回 undefined。 */
export function authoritativeGloss(term: string): string | undefined {
  return AUTHORITATIVE_GLOSS[term.trim()];
}

/** 将任意位置字符串规范化为中文 */
export function positionZh(raw: string | null | undefined): string {
  if (!raw) return "未知位置";
  const trimmed = raw.replace(/[（(][^）)]*[）)]/g, "").trim();
  if (!trimmed) return "未知位置";
  // 数据占位枚举(unknown)不得裸露给学习者(r1 评审阻塞:/stats 记忆成长露「unknown」)
  if (trimmed.toLowerCase() === "unknown") return "未知位置";
  if (POSITION_ZH[trimmed]) return POSITION_ZH[trimmed];
  // 尝试匹配已知缩写
  for (const [key, zh] of Object.entries(POSITION_ZH)) {
    if (trimmed.toUpperCase().includes(key.toUpperCase())) return zh;
  }
  // 已含中文则直接返回
  if (/[\u4e00-\u9fa5]/.test(trimmed)) return trimmed;
  return trimmed;
}

/** 剥离字段里的括号注释（如 OCR 校正备注），保留原始记法本身 */
export function stripAnnotation(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw.replace(/[（(][^）)]*[）)]/g, "").trim();
}

/** 将任意街字符串规范化为中文 */
export function streetZh(raw: string | null | undefined): string {
  if (!raw) return "未知街";
  const trimmed = raw.trim();
  if (trimmed.toLowerCase() === "unknown") return "未知街";
  return STREET_ZH[trimmed] || raw;
}

/** 将任意动作字符串规范化为中文 */
export function actionZh(raw: string | null | undefined): string {
  if (!raw) return "未知动作";
  const trimmed = raw.trim().toLowerCase();
  if (ACTION_ZH[trimmed]) return ACTION_ZH[trimmed];
  if (/[\u4e00-\u9fa5]/.test(raw)) return raw;
  return raw;
}

/**
 * 显示层专属场景补充译(不入 SCENARIO_ZH/白名单/重生成契约):
 * - rfi:数据枚举值,契约白名单未收录,仅屏上中文化(r1 评审阻塞 chip 裸露);
 * - 3bet_pot/-pot:表内旧译「3bet 底池」会把 3bet 带进学习者屏(DOM 纯度),
 *   显示层先行拦截为纯中文「再加注底池」(释义表与白名单不动)。
 */
const SCENARIO_DISPLAY_ONLY_ZH: Record<string, string> = {
  rfi: "率先加注",
  "3bet_pot": "再加注底池",
  "3bet-pot": "再加注底池",
};

/** 将概念 key 规范化为中文 */
export function conceptZh(raw: string | null | undefined): string {
  if (!raw) return "通用";
  const trimmed = raw.trim().toLowerCase();
  // 数据占位枚举(general/unknown/other)不得裸露给学习者(r1 评审阻塞)
  if (trimmed === "general" || trimmed === "unknown" || trimmed === "other") return "通用";
  if (SCENARIO_DISPLAY_ONLY_ZH[trimmed]) return SCENARIO_DISPLAY_ONLY_ZH[trimmed];
  if (CONCEPT_ZH[trimmed]) return CONCEPT_ZH[trimmed];
  if (SCENARIO_ZH[trimmed]) return SCENARIO_ZH[trimmed];
  if (/[\u4e00-\u9fa5]/.test(raw)) return raw;
  return raw;
}

/** 场景概念 key（street:pos:scenario）→ 可读中文 */
export function conceptKeyZh(key: string): string {
  const parts = key.split(":");
  if (parts.length < 2) return key;
  const street = streetZh(parts[0]);
  const pos = positionZh(parts[1]);
  const scenario = parts.length > 2 && parts[2] ? conceptZh(parts[2]) : "通用";
  return `${street} · ${pos} · ${scenario}`;
}

/** 引用 chip 三 facet 的学习者中文标签(r1 评审阻塞:position:BB 等英文数据键不得裸露)。 */
export const FACET_LABEL_ZH: Record<string, string> = {
  position: "位置",
  street: "街",
  scenario: "场景",
};

/**
 * 引用 chip facet 值 → 学习者可读纯中文(显示层单一之家)。
 * 未映射/含拉丁残留的值一律收口为纯中文占位,绝不回吐原始英文数据键。
 */
export function conceptFacetZh(facet: string, value: string): string {
  const v = (value ?? "").trim();
  if (facet === "position") {
    const zh = positionZh(v);
    return /[A-Za-z]/.test(zh) ? "未知位置" : zh;
  }
  if (facet === "street") {
    const zh = streetZh(v);
    return /[A-Za-z]/.test(zh) ? "未知街" : zh;
  }
  if (facet === "scenario") {
    const zh = conceptZh(v);
    return /[A-Za-z]/.test(zh) ? "常见场景" : zh;
  }
  if (/[\u4e00-\u9fa5]/.test(v)) return v;
  return "要点";
}

/** 新手友好的动作解释 */
export const ACTION_EXPLAIN: Record<string, string> = {
  fold: "放弃这手牌，不再投入筹码",
  check: "不加注，把决定权留给对手",
  call: "跟注对手的下注金额",
  bet: "主动下注，建立底池或诈唬",
  raise: "提高下注金额，施加压力",
  allin: "押上全部筹码",
};

/**
 * 下注尺度分档决策 cue(B1 强化 / D'③):尺寸按钮各一句白话,讲清「决策差异」而非仅风险档——
 * 小注=少输少赢·要价值,大注=逼他弃牌·施压。让 小注 vs 大注 是目的差异,新手一眼分辨。
 * 克制:短句小字,不堆长文(§0)。键 = Sizing 分数;未列档位回落 ACTION_EXPLAIN.bet。
 */
export const SIZING_CUE: Record<string, string> = {
  "1/4": "少输少赢·向弱牌要价值",
  "1/3": "少输少赢·向弱牌要价值",
  "1/2": "价值与诈唬兼顾·施加压力",
  "2/3": "逼他弃牌·给足压力让对手难跟",
  "3/4": "逼他弃牌·给足压力让对手难跟",
};

/**
 * 决策问句里的动作词 ↔ 按钮术语对齐(新手探针缺陷 O)。
 * 问句常问「平跟」,按钮只有「跟注」——点chip即见白话+对应按钮,消除「对不上」。
 */
export const ACTION_TERM_GLOSS: GlossEntry[] = [
  { term: "平跟入池", gloss: "只跟入、不加注——想这么打就选「跟注」" },
  { term: "平跟", gloss: "只跟入、不加注——想这么打就选「跟注」" },
  { term: "跟注", gloss: "匹配当前下注额" },
  { term: "加注", gloss: "提高下注额" },
  { term: "全下", gloss: "押上全部筹码" },
  { term: "过牌", gloss: "不投入筹码，把决定权留给对手" },
  { term: "下注", gloss: "主动投入筹码" },
  { term: "弃牌", gloss: "放弃这手牌，不再投入" },
];

/**
 * 位置名首次白话(新手探针:3/3 列位置名为生词)。
 * 呈现层点 chip 释义一次即可,不必每个座位都挂。
 */
export const POSITION_GLOSS: GlossEntry[] = [
  { term: "枪口位", gloss: "翻前最先行动的位置" },
  { term: "枪口+1", gloss: "枪口位的下一位,同样偏早" },
  { term: "劫机位", gloss: "比枪口位晚两位,仍偏早" },
  { term: "劫持位", gloss: "比枪口位晚两位,仍偏早" },
  { term: "关煞位", gloss: "中间位置(庄位的前一位)" },
  { term: "中位", gloss: "中间位置" },
  { term: "庄位", gloss: "庄家位（BTN）、翻后最后行动（是位置名，不是屏幕按钮）" },
  { term: "小盲", gloss: "强制下注半个盲注的位置" },
  { term: "大盲", gloss: "强制下注一个盲注的位置" },
  { term: "前位", gloss: "靠前行动的位置,信息少" },
  { term: "后位", gloss: "靠后行动的位置,信息多" },
];

/** 决策屏题面专用合并表:动作词 + 位置名,一次点词释义全覆盖 */
export const DECISION_TERM_GLOSS: GlossEntry[] = [
  ...ACTION_TERM_GLOSS,
  ...POSITION_GLOSS,
];

/**
 * P0 缺词(新手探针 unknown TOP + 同花消歧):全白话,不术语解释术语(D' 术语密度整改)。
 * 同花色/单花面 = C' 消歧锚点(与已成「同花」区分);X池 = 下注尺度白话;位置/牌力/对手画像 = 高频卡壳词。
 */
const DECISION_SCREEN_EXTRA_GLOSS: GlossEntry[] = [
  { term: "同花色", gloss: "两张手牌同一花色（如都是黑桃）——只是花色相同，还没成同花" },
  { term: "单花面", gloss: "三张公共牌全是同一花色" },
  { term: "听花", gloss: "再来一张同花色就凑成同花" },
  { term: "1/3池", gloss: "下注约底池的 1/3（小注）" },
  { term: "2/3池", gloss: "下注约底池的 2/3（中注）" },
  { term: "3/4池", gloss: "下注约底池的 3/4（大注）" },
  { term: "半池", gloss: "下注约底池的一半" },
  { term: "满池", gloss: "下注约整个底池那么大" },
  { term: "一点五倍", gloss: "下注约底池的 1.5 倍（比底池还大，叫超池）" },
  { term: "紧弱", gloss: "一种很少出手、出手多是强牌的对手打法（看到 ta 加注要小心）" },
  { term: "再开一枪", gloss: "这条街再主动下注一次" },
  { term: "溜入池", gloss: "不加注，只跟入大盲看牌" },
  { term: "有利位置", gloss: "你比对手后行动，能先看对手怎么动再决定（信息多，更好打）" },
  { term: "空手", gloss: "没凑成对子/顺子/同花，手里只有高牌" },
  { term: "最大对子", gloss: "你的对子是牌面上最大的一对（≈顶对）" },
  { term: "枪口旁位", gloss: "枪口位旁边（UTG+1），仍然很早行动" },
  { term: "开池", gloss: "全场第一个加注入局" },
  // D'① P1 看懂崩盘高频术语(新手零先验白话,不术语解释术语)
  { term: "中对", gloss: "你凑成的对子，点数在公共牌里算中等（不是最大也不是最小）" },
  { term: "边牌", gloss: "对子一样大时，比谁手里另一张单牌更大（决定输赢的配角牌）" },
  { term: "踢脚", gloss: "对子一样大时，比谁手里另一张单牌更大（决定输赢的配角牌）" },
  { term: "诈唬", gloss: "牌不好也下注/加注，想让对手误以为你牌强而弃牌" },
  { term: "高牌", gloss: "还没凑成对子，手里最大的单牌" },
  { term: "顺子", gloss: "五张点数连续的牌（如 5-6-7-8-9）" },
  { term: "T", gloss: "牌面点数 10 的记法（T = Ten，如 T9 = 10 和 9）" },
  { term: "很牢", gloss: "口语，形容对手打法很稳、不轻易弃牌（很难诈唬走）" },
  // D'① 按钮位消歧:位置名 button 与屏幕按钮撞名(P1/P3 实测双双误读)→ 显示「庄家位(BTN)」
  { term: "庄家位(BTN)", gloss: "庄家位（BTN）：最后行动的位置，信息最多、最好打（是位置名，不是屏幕按钮）" },
  { term: "庄家位", gloss: "庄家位（BTN）：最后行动的位置，信息最多、最好打（是位置名，不是屏幕按钮）" },
  // D'① 行动线高频口语(P1 confusion 实测卡壳)
  { term: "翻牌再加", gloss: "翻牌时对手把你的下注又加了回来（往往代表牌很强）" },
  { term: "逼弃", gloss: "下注逼对手弃牌，想直接收下底池" },
  { term: "被跟", gloss: "你下注后，对手跟注了（没有弃牌）" },
  { term: "控池", gloss: "故意把底池控小，避免拿中等牌打大底池" },
  { term: "中等对子", gloss: "不大不小的对子（点数在牌面中间，容易输更大的对子）" },
  { term: "小对子", gloss: "很小的对子（如 22-66），主要靠凑成三条赚钱" },
  { term: "三人池", gloss: "三个人一起争的底池（诈唬更难成功，要更谨慎）" },
  { term: "未知位置", gloss: "这条记录没标具体座位，不影响看懂行动本身" },
];

/** 决策屏全量释义表(D'):动作词 + 位置名 + P0 缺词,标题/行动线一次点词全覆盖 */
export const DECISION_SCREEN_GLOSS: GlossEntry[] = [
  ...DECISION_TERM_GLOSS,
  ...DECISION_SCREEN_EXTRA_GLOSS,
];

/**
 * 题眼术语表(F3):标题命中决策关键术语时,取「词 + 白话」提示对落到首屏提示行
 * (不再把白话内联进标题——标题保持紧凑,白话复用已有提示行,净增文字为零)。
 * P1 实测病灶:新手扫一眼不点 chip,术语在、白话不在首屏
 * (generated 6 失败屏:325 很牢+诈唬 / 319 中对 / 062 边牌 / 032 同花色 / 121 中对 / 215 逼弃)。
 *
 * 克制(§0):只收失败屏点名的题眼术语(不覆盖所有词);白话取 DECISION_SCREEN_GLOSS
 * 同义的短语级压缩(「中等对子」而非长句),长释义仍留给标题上可点 chip 深挖。
 */
export const TITLE_INLINE_TERMS: Readonly<Record<string, string>> = {
  同花色: "两张手牌同花色",
  中对: "中等对子",
  边牌: "比大小的单牌",
  很牢: "很少弃牌",
  诈唬: "没牌也下注骗弃",
  逼弃: "下注逼他弃",
  紧弱: "很少出手、出手多强牌",
  顺子: "五张连续",
  高牌: "没成对的最大单牌",
};

/**
 * 题眼术语扫描式(单遍):matchAll 不回扫,避免命中结果里的词被二次命中。
 * 守护铁律:
 *  - 「同花色手牌+ranks」(sanitizeSuitedZh 产物)整词优先,点数并入,提示词含点数
 *    (不切碎牌型:命中「同花色手牌67」而非「同花色」);
 *  - (?<!张) 排除「三张同花色的牌面」(单花面消毒产物,讲的是牌面不是手牌);
 *  - (?<![半抓])诈唬:半诈唬/抓诈唬是独立概念,白话不适用;
 *  - 顺子(?!听):「顺子听牌」是复合词,不切碎。
 */
const TITLE_INLINE_RE =
  /(?<!张)同花色手牌[0-9TJQKA]{0,2}|(?<!张)同花色(?![手])[0-9TJQKA]{0,2}|(?<![半抓])诈唬|顺子(?!听)|中对|边牌|很牢|逼弃|紧弱|高牌/g;

/**
 * 纯函数(F3):标题题眼术语 → 「词 + 白话」提示对(供首屏提示行渲染「词 = 白话」)。
 * 去重(同术语只取首次)+ 截前 2 条(防多术语标题把提示行撑成第二密度源)。
 * 边界守护原样继承:半诈唬/抓诈唬/张同花色/顺子听 → 无 hint。
 */
export function titleTermHints(title: string | null | undefined): { term: string; gloss: string }[] {
  if (!title) return [];
  const seen = new Set<string>();
  const out: { term: string; gloss: string }[] = [];
  for (const m of title.matchAll(TITLE_INLINE_RE)) {
    const matched = m[0];
    const key = matched.startsWith("同花色") ? "同花色" : matched;
    const gloss = TITLE_INLINE_TERMS[key];
    if (!gloss || seen.has(key)) continue;
    seen.add(key);
    out.push({ term: matched, gloss });
    if (out.length >= 2) break;
  }
  return out;
}

/**
 * 决策屏顶栏对手位呈现决策(U1 · 组合α):villain 为空时不渲「vs 未知位置」
 * (P1/P3 实测红字困惑:不知道的对手位置没有信息量,反而制造疑问)。
 *  - villain 有值 → 照常 vs;
 *  - villain 空 + 行动线含多人信息(三人/两人/多人)→ 「多人底池」;
 *  - villain 空 + 无多人信息 → 只显示 hero 位置。
 */
export type VillainLabelKind = "villain" | "multiway" | "hidden";

const MULTIWAY_RE = /三人|两人|多人/;

export function villainLabelKind(
  villainPosition: string | null | undefined,
  actionLine: string | null | undefined,
): VillainLabelKind {
  if (villainPosition && villainPosition.trim()) return "villain";
  if (actionLine && MULTIWAY_RE.test(actionLine)) return "multiway";
  return "hidden";
}

/**
 * 顶栏位置纯文本(probe/persona 所见即 UI 判定同源:同走 villainLabelKind)。
 * UI 侧(spot-prompt)因位置名挂 chip 单独渲染,但分支判定必须与本函数一致。
 */
export function positionLabelText(
  heroPosition: string | null | undefined,
  villainPosition: string | null | undefined,
  actionLine: string | null | undefined,
): string {
  const hero = positionZh(heroPosition ?? "");
  const kind = villainLabelKind(villainPosition, actionLine);
  if (kind === "villain") return `${hero} vs ${positionZh(villainPosition)}`;
  if (kind === "multiway") return `${hero} · 多人底池`;
  return hero;
}

/**
 * BB 数字语境一眼化(D'):「56.5 BB」→「56.5 BB（56.5 个大盲）」、「≈1.5BB」→「≈1.5BB（1.5 个大盲）」。
 * 内联白话「个大盲」不点 chip 即懂;括号内回填具体倍数数字(判官 r24-r32 连续 9 轮口径);
 * 原串保留为子串(BB 可点 chip 仍生效);幂等护栏:已带「（数字…个大盲」后缀的不再二次包裹。
 */
export function bbInline(text: string): string {
  return text.replace(
    /(\d+(?:\.\d+)?)\s*(BB)(?!\s*（\s*\d)/gi,
    (m, num: string) => `${m}（${num} 个大盲）`,
  );
}

/**
 * 底池/后手缺数据时的盲注基准估算(仅呈现层,绝不写回数据)。
 * 338 场景中 325 场景 pot 为 null——新手探针 3/3 卡在「底池多少」无数字可答。
 * 数据缺 → 按街给盲注基准的保守估算,并显式标「估算」(诚实,不伪精确):
 * preflop = 1.5BB(SB 0.5 + BB 1.0),之后每街约加一个小额下注(≈3BB)。
 */

const INFERRED_POT_BB: Record<string, number> = {
  preflop: 1.5,
  flop: 4.5,
  turn: 7.5,
  river: 10.5,
};

const DEFAULT_POT_BB = 1.5;

/** 常规局默认后手深度(BB) */
export const INFERRED_STACK_BB = 100;

export type PotView = {
  /** 展示数字(带单位),如 "56.5 BB" / "≈1.5BB" */
  text: string;
  /** true = 盲注基准估算,UI 须标「估算」 */
  inferred: boolean;
};

/** 底池展示:有真值用真值;缺数据 → 按街盲注基准估算 */
export function potView(pot: string | null | undefined, street?: string | null): PotView {
  if (pot && pot.trim()) return { text: pot.trim(), inferred: false };
  const bb = INFERRED_POT_BB[street ?? ""] ?? DEFAULT_POT_BB;
  return { text: `≈${bb}BB`, inferred: true };
}

export type StacksView = {
  text: string;
  inferred: boolean;
};

/** 后手展示:有真值用真值;缺数据 → 常规 100BB 粗估 */
export function stacksView(stacks: string | null | undefined): StacksView {
  if (stacks && stacks.trim()) return { text: stacks.trim(), inferred: false };
  return { text: `≈${INFERRED_STACK_BB}BB`, inferred: true };
}
